import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BookOpen, Eye, Trash2, CalendarDays, TableIcon, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { BookingsCalendar } from "./BookingsCalendar";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-500/15 text-blue-700 border-blue-200",
  in_progress: "bg-purple-500/15 text-purple-700 border-purple-200",
  completed: "bg-green-500/15 text-green-700 border-green-200",
  cancelled: "bg-red-500/15 text-red-700 border-red-200",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const paymentMethodLabels: Record<string, string> = {
  cash: "Cash",
  paypal: "PayPal",
  stripe: "Card",
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground font-sans">{label}</span>
      <span className="col-span-2 text-sm font-sans text-foreground">{value}</span>
    </div>
  );
}

export function BookingsSection() {
  const [rows, setRows] = useState<any[]>([]);
  const { toast } = useToast();

  const load = async () => {
    const { data, error } = await supabase.from("bookings").select("*, vehicles(name)").order("created_at", { ascending: false });
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setRows(data || []);
  };

  useEffect(() => { load(); }, []);

  const updateField = async (id: string, field: string, value: string) => {
    const { error } = await supabase.from("bookings").update({ [field]: value } as any).eq("id", id);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    load();
  };

  const deleteBooking = async (id: string) => {
    const { error } = await supabase.from("bookings").delete().eq("id", id);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Deleted", description: "Booking has been removed." });
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Bookings</h2>
          <p className="text-muted-foreground text-sm mt-1">Manage reservations <Badge variant="secondary" className="ml-2">{rows.length}</Badge></p>
        </div>
      </div>

      <Tabs defaultValue="table" className="w-full">
        <TabsList>
          <TabsTrigger value="table" className="gap-1.5">
            <TableIcon className="h-4 w-4" /> Table
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-1.5">
            <CalendarDays className="h-4 w-4" /> Calendar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="table">
          <Card>
            <CardContent className="p-0">
              {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <BookOpen className="h-10 w-10 mb-3 opacity-40" /><p>No bookings yet</p>
                </div>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Route</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow key={row.id}>
                          <TableCell className="font-mono text-xs font-bold text-primary">{row.booking_code || "—"}</TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">{row.customer_name}</div>
                            <div className="text-xs text-muted-foreground">{row.customer_phone}</div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs max-w-[120px] truncate">{row.pickup_location}</div>
                            <div className="text-xs max-w-[120px] truncate text-muted-foreground">→ {row.dropoff_location}</div>
                            <Badge variant="secondary" className="text-[10px] mt-1">{row.route_type}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">{(row.vehicles as any)?.name || "—"}</TableCell>
                          <TableCell className="font-semibold text-green-700">${row.total_price}</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <Badge variant="outline" className={row.payment_status === "paid" ? "bg-green-500/15 text-green-700 border-green-200" : "bg-yellow-500/15 text-yellow-700 border-yellow-200"}>
                                {row.payment_status}
                              </Badge>
                              <div className="text-[10px] text-muted-foreground">{paymentMethodLabels[row.payment_method] || row.payment_method || "—"}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select value={row.status} onValueChange={v => updateField(row.id, "status", v)}>
                              <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(statusLabels).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-xs">{format(new Date(row.created_at), "MMM d, yyyy")}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="h-4 w-4" /></Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle className="font-display">Booking Details</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-1">
                                    <DetailRow label="Booking Code" value={<span className="font-mono font-bold text-primary">{row.booking_code || "—"}</span>} />
                                    <DetailRow label="Customer" value={row.customer_name} />
                                    <DetailRow label="Email" value={row.customer_email} />
                                    <DetailRow label="Phone" value={row.customer_phone} />
                                    <DetailRow label="Pickup" value={row.pickup_location} />
                                    <DetailRow label="Drop-off" value={row.dropoff_location} />
                                    <DetailRow label="Route Type" value={row.route_type} />
                                    <DetailRow label="Pickup Date" value={format(new Date(row.pickup_date), "PPP 'at' p")} />
                                    {row.return_date && <DetailRow label="Return Date" value={format(new Date(row.return_date), "PPP 'at' p")} />}
                                    <DetailRow label="Passengers" value={row.passengers} />
                                    <DetailRow label="Vehicle" value={(row.vehicles as any)?.name} />
                                    <DetailRow label="Distance" value={row.distance_km ? `${row.distance_km} km` : null} />
                                    <DetailRow label="Est. Time" value={row.estimated_time_min ? `${row.estimated_time_min} min` : null} />
                                    <DetailRow label="Flight" value={row.flight_number} />
                                    <DetailRow label="Special Requests" value={row.special_requests} />
                                    <DetailRow label="Payment Method" value={paymentMethodLabels[row.payment_method] || row.payment_method} />
                                    <DetailRow label="Transaction ID" value={row.stripe_payment_id ? <span className="font-mono text-xs">{row.stripe_payment_id}</span> : null} />
                                    <DetailRow label="Payment Status" value={
                                      <Select value={row.payment_status} onValueChange={v => updateField(row.id, "payment_status", v)}>
                                        <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="unpaid">Unpaid</SelectItem>
                                          <SelectItem value="pending">Pending</SelectItem>
                                          <SelectItem value="paid">Paid</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    } />
                                    <DetailRow label="Status" value={
                                      <Badge variant="outline" className={statusColors[row.status] || ""}>
                                        {statusLabels[row.status] || row.status}
                                      </Badge>
                                    } />
                                    <DetailRow label="Total" value={<span className="font-bold text-green-700">${row.total_price}</span>} />
                                    <DetailRow label="Created" value={format(new Date(row.created_at), "PPP 'at' p")} />
                                  </div>
                                  <DialogFooter>
                                    <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>

                              {row.booking_code && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                  <a href={`/voucher/${row.booking_code}`} target="_blank" rel="noopener noreferrer" title="View public voucher">
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                </Button>
                              )}

                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Booking?</AlertDialogTitle>
                                    <AlertDialogDescription>This action cannot be undone. The booking for {row.customer_name} will be permanently removed.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => deleteBooking(row.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar">
          <BookingsCalendar rows={rows} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
